import { Disclaimer } from '@/components/life/Disclaimer';
import { Hero } from '@/components/life/Hero';
import Layout from '@/components/shared/Layout';
import { SectionEventWrapper } from '@/components/shared/SectionEventWrapper';
import { SEO } from '@/components/shared/SEO';
import type { Config } from '@/pageConfig/life';
import { LIFE_LANDING_PAGE } from '@/utils/productAdaPages';
import { PRODUCT_LIFE } from '@/utils/products';
import { Flex } from '@lemonade-hq/cdk';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import dynamic from 'next/dynamic';
import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

const Knowledge = dynamic(() => import('@/components/life/Knowledge'), {
  ssr: false,
});
const Trusted = dynamic(() => import('@/components/life/Trusted'));
const Coverages = dynamic(() => import('@/components/life/Coverages'));

const DESKTOP_TOP_SECTION_MARGIN = 120;
const MOBILE_TOP_SECTION_MARGIN = 35;

export const LifePageWrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <Flex
      textAlign="center"
      flexDirection="column"
      alignItems="center"
      mt={[MOBILE_TOP_SECTION_MARGIN + 10, MOBILE_TOP_SECTION_MARGIN + 10, DESKTOP_TOP_SECTION_MARGIN]}
      px={[20, 20, 0]}
    >
      {children}
    </Flex>
  );
};

interface LifePageTemplateProps {
  config: Config;
}

export const LifePageTemplate: React.FC<LifePageTemplateProps> = ({ config }) => {
  const { ref: middlePageRef, inView: middlePageInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const { ref: endPageRef, inView: endPageInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const { trackEvent } = useAnalyticsContext();

  const analyticsParams = useMemo(
    () => ({
      product: PRODUCT_LIFE,
      region: 'pending',
      client_uuid: 'pending',
    }),
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: we don't need trackEvent as dep here
  useEffect(() => {
    if (endPageInView) {
      trackEvent('life_page.scrolling_100%.viewed', analyticsParams);
    }
    if (middlePageInView) {
      trackEvent('life_page.scrolling_50%.viewed', analyticsParams);
    }
  }, [middlePageInView, endPageInView, analyticsParams]);

  return (
    <Layout
      headerActions={config.layout.headerActions}
      product={PRODUCT_LIFE}
      alignLogoOnMobile={config.layout.alignLogoOnMobile}
      page={LIFE_LANDING_PAGE}
      productType={PRODUCT_LIFE}
      showDisclaimer={false}
      additionalFooterContent={<Disclaimer />}
      floatingCheckOurPricesInMobile={config.layout.floatingCheckOurPricesInMobile}
    >
      <SEO {...config.seo} />
      <LifePageWrapper>
        <SectionEventWrapper name="Hero" page="life" position={1}>
          <Hero {...config.hero} />
        </SectionEventWrapper>
        <SectionEventWrapper name="Trusted" page="life" position={2}>
          <Trusted {...config.trusted} />
        </SectionEventWrapper>
        <SectionEventWrapper name="Coverages" page="life" position={3}>
          <Coverages {...config.coverages} />
        </SectionEventWrapper>
        <div ref={middlePageRef} />
        <SectionEventWrapper name="Knowledge" page="life" position={4}>
          <Knowledge />
        </SectionEventWrapper>
        <div ref={endPageRef} />
      </LifePageWrapper>
    </Layout>
  );
};
